import { Button as BaseButton } from "@base-ui/react/button";
import type { ComponentProps, ReactNode } from "react";
import { Link, type LinkProps } from "react-router";
import { cx } from "./cx.js";
import { SpinnerIcon } from "./icons.jsx";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "link";
export type ButtonSize = "sm" | "md";

interface StyleProps {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  /** Leading icon slot. */
  icon?: ReactNode;
}

export function buttonClass({ variant = "secondary", size = "md" }: StyleProps, extra?: string) {
  return cx("dt-btn", `dt-btn--${variant}`, `dt-btn--${size}`, extra);
}

export interface ButtonProps extends Omit<ComponentProps<typeof BaseButton>, "className">, StyleProps {
  className?: string | undefined;
  /** Shows a spinner and disables the button. */
  loading?: boolean | undefined;
}

/** Base UI Button with Diotia variants. */
export function Button({ variant, size, icon, loading, className, disabled, children, ...props }: ButtonProps) {
  return (
    <BaseButton className={buttonClass({ variant, size }, className)} disabled={disabled || loading} data-loading={loading || undefined} {...props}>
      {loading ? <SpinnerIcon /> : icon}
      {children}
    </BaseButton>
  );
}

export interface LinkButtonProps extends LinkProps, StyleProps {}

/** A router Link that looks like a Button (kept as an `<a>` for semantics). */
export function LinkButton({ variant, size, icon, className, children, ...props }: LinkButtonProps) {
  return (
    <Link className={buttonClass({ variant, size }, className)} {...props}>
      {icon}
      {children}
    </Link>
  );
}
